/**
 * Wraps a route subtree and redirects to /login if the user isn't
 * signed in. The current pathname is round-tripped via the `next`
 * query param so /api/auth/google/callback can land us back where we
 * started.
 *
 * Role gating:
 *   requireRole="admin"                 — only admins pass
 *   requireRole={['admin','viewer']}    — staff roles pass, occupants don't
 *   requireOccupant                     — only occupants pass
 *
 * Cross-role redirects:
 *   - An `occupant` who hits an admin/staff route → /portal
 *   - A staff user (admin or viewer) who hits an occupant-only route → /dashboard
 *   - An unrecognized role or no matching gate → /dashboard (safe default)
 */

import { Navigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/data/auth'

export default function AuthGate({
  children,
  requireRole,
  requireOccupant = false,
  skipPasswordCheck = false,
}) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-navy-900 text-white/30 font-mono text-xs">
        loading…
      </div>
    )
  }

  if (!user) {
    const nextUrl = location.pathname + location.search
    return <Navigate to={`/login?next=${encodeURIComponent(nextUrl)}`} replace />
  }

  // Force temp-password holders to /change-password before they see any
  // dashboard content. skipPasswordCheck is set on the /change-password
  // route itself so we don't create a redirect loop.
  if (!skipPasswordCheck && user.must_change_password) {
    return <Navigate to="/change-password" replace />
  }

  const isOccupant = user.role === 'occupant'

  // Occupant-only route: only occupants pass, staff bounce to their dashboard.
  if (requireOccupant) {
    if (isOccupant) return children
    return <Navigate to="/dashboard" replace />
  }

  // Staff-only or role-array gate.
  if (requireRole) {
    const allowed = Array.isArray(requireRole) ? requireRole : [requireRole]
    if (allowed.includes(user.role)) return children
    // Occupants who wandered into an admin route land on their portal,
    // not on /login (they ARE signed in — just to the wrong surface).
    if (isOccupant) return <Navigate to="/portal" replace />
    return <Navigate to="/dashboard" replace />
  }

  // No explicit gate — still keep occupants out of the admin app if
  // they hit a generic AuthGate-wrapped route (belt-and-suspenders).
  if (isOccupant && !location.pathname.startsWith('/portal')) {
    return <Navigate to="/portal" replace />
  }

  return children
}
